import React, { useEffect, useState } from 'react';
import { Download, Share } from 'lucide-react';
import BrandLogo from '../brand/BrandLogo';

// Deterministic seller Home-Screen installation prompt (Jun 2026).
// After the seller lands in the dashboard: if FABTEXS AI is NOT already
// installed AND installation is supported, show a prominent prompt.
// No "Maybe Later" — the seller is expected to install the app. Uses the
// browser's REAL install flow (native `beforeinstallprompt` on Android/Chrome/
// Edge; Share → Add to Home Screen instructions on iOS Safari). Never fakes it.
// Installation and push-notification permission are kept fully separate.

const isStandalone = () =>
  (typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true));

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream;

const isSafari = () =>
  typeof navigator !== 'undefined' &&
  /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

export default function SellerInstallPrompt() {
  const [deferred, setDeferred] = useState(() => (typeof window !== 'undefined' ? window.__fabInstallPrompt : null) || null);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(false); // per-session only; reappears next login

  useEffect(() => {
    const onAvail = () => setDeferred(window.__fabInstallPrompt || null);
    const onInstalled = () => setInstalled(true);
    window.addEventListener('fab-install-available', onAvail);
    window.addEventListener('fab-installed', onInstalled);
    // In case the event fired before this component mounted.
    if (window.__fabInstallPrompt) setDeferred(window.__fabInstallPrompt);
    return () => {
      window.removeEventListener('fab-install-available', onAvail);
      window.removeEventListener('fab-installed', onInstalled);
    };
  }, []);

  const iosInstall = isIOS() && isSafari();
  // Show only when NOT installed, not dismissed this session, and we have a
  // real install path (a captured native prompt, or iOS Safari instructions).
  const show = !installed && !dismissed && (deferred || iosInstall);
  if (!show) return null;

  const onInstall = async () => {
    if (!deferred) return; // iOS: instructions are shown instead
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      window.__fabInstallPrompt = null;
      setDeferred(null);
      if (choice?.outcome === 'accepted') setInstalled(true);
      else setDismissed(true); // native dialog cancelled — hide for this session
    } catch (_e) {
      setDismissed(true);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="seller-install-prompt">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 relative fade-up">
        <div className="flex justify-center mb-4"><BrandLogo height={34} /></div>
        <h2 className="text-center font-display text-xl font-bold text-fab-text">Install FABTEXS AI</h2>
        <p className="text-center text-[13px] text-fab-text-secondary mt-2">
          To manage your business, orders, buyers and notifications, add FABTEXS AI to your Home Screen.
        </p>

        {iosInstall && !deferred ? (
          <div className="mt-4 rounded-xl bg-fab-elevated p-4 text-[13px] text-fab-text-secondary space-y-2" data-testid="seller-install-ios-steps">
            <div className="flex items-center gap-2"><Share className="w-4 h-4 text-fab-primary" /> Tap the <span className="font-semibold">Share</span> icon in Safari</div>
            <div className="flex items-center gap-2"><Download className="w-4 h-4 text-fab-primary" /> Choose <span className="font-semibold">Add to Home Screen</span></div>
          </div>
        ) : (
          <button
            onClick={onInstall}
            data-testid="seller-add-to-home-screen-btn"
            className="btn-primary w-full mt-5 text-[14px] flex items-center justify-center gap-2 py-3"
          >
            <Download className="w-4 h-4" /> Add to Home Screen
          </button>
        )}
      </div>
    </div>
  );
}
